<template>
  <form
    novalidate
    class="flex min-h-full min-w-full grow flex-col"
    @submit="onSubmit"
  >
    <div class="mb-6 flex-1 space-y-6">
      <div class="flex w-full items-center justify-center">
        <div
          class="relative flex size-12 items-center justify-center rounded bg-primary text-white"
          :style="{
            backgroundColor:
              productPreviewColor || undefined,
          }"
        >
          {{ productPreviewShortString }}
        </div>
      </div>
      <TextInputFormField
        name="name"
        label="Product Name"
      />

      <BaseColorInput name="color" label="Display Color" />

      <div>
        <BaseButtonSwitchFormField
          name="hasVariablePrice"
          :options="variablePriceOptions"
          class="mb-2 max-w-48"
        />

        <FormInputPrice
          name="price"
          :disabled="values.hasVariablePrice"
        />
      </div>

      <QueryLoading v-if="isCategoriesLoading" />
      <QueryError v-else-if="isCategoriesError">
        Failed to retrieve categories
      </QueryError>
      <BaseSelectInput
        v-else
        :options="categoryOptions"
        label="Category"
        name="parentCategoryId"
      />

      <template v-if="isVatEnabled">
        <FormInputVat name="vatRate" label="VAT" />
      </template>
    </div>

    <ActionButtonPrimary
      type="submit"
      class="mt-auto w-full"
      :disabled="isCategoriesError || isCategoriesLoading"
    >
      Save Product
    </ActionButtonPrimary>
  </form>
</template>

<script setup lang="ts">
import {
  useProductForm,
  type ProductFormOuput,
} from "@/forms/productForm"
import { useCategories } from "@/services/categories/listCategories"
import { retrieveProduct } from "@/services/products/retrieveProduct"

const props = defineProps<{
  productId?: string
}>()

const emit = defineEmits<{
  (event: "submit", params: ProductFormOuput): void
}>()

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { isVatEnabled } = useTenant()
const {
  asOptions: categoryOptions,
  getById,
  isError: isCategoriesError,
  isLoading: isCategoriesLoading,
} = useCategories()

const { handleSubmit, values, setValues } = useProductForm()

const onSubmit = handleSubmit(async (params) => {
  emit("submit", params)
})

onMounted(async () => {
  if (!props.productId) return

  startLoading()
  try {
    const productResponse = await retrieveProduct(
      props.productId
    )

    setValues(productResponse)
  } finally {
    stopLoading()
  }
})

const parentCategory = computed(
  () => getById(values.parentCategoryId ?? null).value
)

const productPreviewColor = computed(
  () => values.color || parentCategory.value?.color
)

const productPreviewShortString = computed(() =>
  buildShortString(values.name ?? "")
)

const variablePriceOptions = [
  {
    value: false,
    label: "Fixed Price",
  },
  {
    value: true,
    label: "Variable Price",
  },
]
</script>
